// OpportunityList.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Button,
  Container,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Card,
  CardContent,
  Grid,
  InputLabel,
  Collapse,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Snackbar,
  Switch,
  Paper,
} from "@mui/material";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ClearIcon from "@mui/icons-material/Clear";
import SortIcon from "@mui/icons-material/Sort";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { categoryApi, researchApi } from "../api";
import Header from "../components/Header";
import { timeAgo } from "../utils/timeAgo";
import BannerImg from "../assets/images/india-rise-banner.png";
import { Edit as EditIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const OpportunityList = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState({
    categories: [],
    subCategories: [],
  });

  const isAdmin = JSON.parse(localStorage.user || "null");
  const navigate = useNavigate();

  // Filter states
  const [filters, setFilters] = useState({
    category: "",
    subCategory: "",
    search: "",
    status: "",
    sortBy: "",
    sortDirection: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);

  const description = `The Resources and Opportunities Database is a centralised, open-access platform designed to support Indian women in STEMM by connecting them to the resources they need to advance their research careers. It brings together both funding opportunities (such as grants, fellowships, scholarships and awards) and non-funding resources (including mentorship programs, leadership training, research tools, networks, policy guides and professional development materials) into one easy-to-navigate hub.
Whether an individual is seeking financial support, skill-building, mentorship, collaboration, or career pathways, the database provides curated, credible and regularly updated listings tailored specifically to the needs of Indian women researchers across all STEMM disciplines.`;

  const fetchCategories = async () => {
    try {
      const result = await categoryApi.getAllCategories();
      if (result.data?.data?.success) {
        setCategories(result.data?.data?.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchOpportunities = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        isAdmin: isAdmin,
        page: currentPage,
        limit: rowsPerPage,
        ...filters,
      };
      const response = await researchApi.getAllOpportunities(payload);
      if (response?.data?.data?.success) {
        setOpportunities(response?.data?.data?.data || []);
        setPagination(
          response?.data?.data.pagination || { total: 0, totalPages: 0 }
        );
      } else {
        setError(response?.error || "Failed to fetch opportunities");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to fetch opportunities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpportunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOpportunities();
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "category" ? { subCategory: "" } : {}),
    }));
  };

  // New function to handle sort change and immediately fetch data
  const handleSortChange = (sortBy, sortDirection) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortDirection,
    }));
    setCurrentPage(1);
  };

  // Effect to fetch opportunities when filters change
  useEffect(() => {
    if (currentPage === 1) {
      fetchOpportunities();
    } else {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.sortBy, filters.sortDirection]);

  const handleToggleStatus = (opp) => {
    setSelectedOpp(opp);
    setDialogOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!selectedOpp) return;
    setDialogOpen(false);
    const newStatus = !selectedOpp.status;
    try {
      const response = await researchApi.updateOpportunityStatus(
        selectedOpp.id,
        {
          status: newStatus,
        }
      );
      if (response?.data?.success) {
        setOpportunities((prev) =>
          prev.map((o) =>
            o.id === selectedOpp.id ? { ...o, status: newStatus } : o
          )
        );
        showSnackbar("Status updated successfully!");
      } else {
        showSnackbar("Failed to update status", "error");
      }
    } catch (error) {
      console.error("Update error:", error);
      setError("Failed to update status");
    }
  };

  const getPageNumbers = () => {
    const totalPages = pagination.totalPages;
    const pages = [];

    if (totalPages <= 5) {
      return [...Array(totalPages)].map((_, i) => i + 1);
    }

    pages.push(1);

    if (currentPage > 3) {
      pages.push("...");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages.filter(
      (page, idx) =>
        idx === 0 ||
        page !== "..." ||
        pages[idx - 1] !== "..." ||
        pages[idx + 1] !== "..."
    );
  };

  const handleEdit = (data) => {
    navigate(`/research/${data.id}`);
  };

  return (
    <>
      <Header title="Resources and Opportunities List" />
      {loading && opportunities.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
            height: "80vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {/* Background Banner */}
            <img
              src={BannerImg}
              alt="Banner"
              style={{
                width: "100%",
                objectFit: "contain",
              }}
            />

            {/* Overlay Text */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 2,
              }}
            >
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  fontSize: {
                    xs: "1rem",
                    sm: "1.2rem",
                    md: "2rem",
                    lg: "3rem",
                  },
                }}
              >
                <Box component="span" sx={{ color: "#ffffff" }}>
                  Resources
                </Box>{" "}
                <Box component="span" sx={{ color: "#FF9E1B" }}>
                  &amp; Opportunities
                </Box>
              </Typography>
            </Box>
          </Box>

          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Description Section - Modern Hero Card */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                bgcolor: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  bgcolor: "primary.main",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: "#1e3a5f",
                      mb: 2,
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                    }}
                  >
                    Empowering Indian Women in STEMM
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.6,
                      color: "text.primary",
                      opacity: 0.9,
                      fontSize: { xs: "0.9rem", md: "1rem" },
                    }}
                  >
                    {description}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexShrink: 0,
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    bgcolor: "rgba(30, 58, 95, 0.05)",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: "primary.main",
                      fontWeight: 700,
                      fontSize: "2.5rem",
                    }}
                  >
                    🚀
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Search Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: "#1e3a5f",
                  mb: 3,
                }}
              >
                Search For
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "stretch", md: "flex-end" },
                }}
              >
                <FormControl fullWidth sx={{ flex: { md: 1 } }}>
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    value={filters.category}
                    label="Category"
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                    sx={{ bgcolor: "white" }}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ flex: { md: 1 } }}>
                  <InputLabel id="subcategory-label">Sub Category</InputLabel>
                  <Select
                    labelId="subcategory-label"
                    value={filters.subCategory}
                    label="Sub Category"
                    onChange={(e) =>
                      handleFilterChange("subCategory", e.target.value)
                    }
                    sx={{ bgcolor: "white" }}
                    disabled={!filters.category}
                  >
                    <MenuItem value="">All Sub Categories</MenuItem>
                    {categories.subCategories
                      .filter((subCat) => subCat.parent_id === filters.category)
                      .map((subCat) => (
                        <MenuItem key={subCat.id} value={subCat.id}>
                          {subCat.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {isAdmin && (
                  <FormControl fullWidth sx={{ flex: { md: 1 } }}>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      value={filters.status}
                      label="Status"
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="1">Active</MenuItem>
                      <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label="Area of Interest"
                  placeholder="Search ..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  sx={{
                    flex: { md: 1.5 },
                    bgcolor: "white",
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{
                    bgcolor: "#1e3a5f",
                    px: 4,
                    py: 1.5,
                    minWidth: { xs: "100%", md: "120px" },
                    "&:hover": {
                      bgcolor: "#152d4a",
                    },
                  }}
                >
                  Search
                </Button>
              </Box>
            </Box>

            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 3,
                bgcolor: "background.paper",
                background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)",
                border: "1px solid",
                borderColor: "primary.light",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                {/* Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: { xs: 2, md: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      bgcolor: "primary.main",
                      color: "white",
                    }}
                  >
                    <SortIcon fontSize="small" />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "primary.main",
                      fontSize: "1.1rem",
                    }}
                  >
                    Sort by Application Deadline
                  </Typography>
                </Box>

                {/* Sort Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    flex: 1,
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  {/* ASC - Earliest First */}
                  <Chip
                    icon={<ArrowUpwardIcon />}
                    label="ASC"
                    variant={
                      filters.sortBy === "lastDateToApply" &&
                      filters.sortDirection === "ASC"
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() => handleSortChange("lastDateToApply", "ASC")}
                    color="primary"
                    sx={{
                      px: 2.5,
                      py: 1.8,
                      fontWeight: 600,
                      borderRadius: "20px",
                      borderWidth: "2px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(30, 58, 95, 0.25)",
                        bgcolor:
                          filters.sortBy === "lastDateToApply" &&
                          filters.sortDirection === "ASC"
                            ? "primary.dark"
                            : "primary.50",
                      },
                    }}
                  />

                  {/* DESC - Latest First */}
                  <Chip
                    icon={<ArrowDownwardIcon />}
                    label="DESC"
                    variant={
                      filters.sortBy === "lastDateToApply" &&
                      filters.sortDirection === "DESC"
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() => handleSortChange("lastDateToApply", "DESC")}
                    color="primary"
                    sx={{
                      px: 2.5,
                      py: 1.8,
                      fontWeight: 600,
                      borderRadius: "20px",
                      borderWidth: "2px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(30, 58, 95, 0.25)",
                        bgcolor:
                          filters.sortBy === "lastDateToApply" &&
                          filters.sortDirection === "DESC"
                            ? "primary.dark"
                            : "primary.50",
                      },
                    }}
                  />

                  {/* Clear Sort */}
                  <Chip
                    icon={<RestartAltIcon />}
                    label="Default"
                    variant="outlined"
                    onClick={() => handleSortChange("", "")}
                    sx={{
                      px: 2.5,
                      py: 1.8,
                      fontWeight: 600,
                      borderRadius: "20px",
                      borderWidth: "2px",
                      borderColor: "#666",
                      color: "#666",
                      "&:hover": {
                        bgcolor: "#666",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(102, 102, 102, 0.25)",
                      },
                      "& .MuiChip-icon": {
                        ml: 0.5,
                      },
                    }}
                  />
                </Box>

                {/* Active Sort Indicator */}
                {filters.sortBy && filters.sortDirection && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      ml: { xs: 0, md: "auto" },
                      mt: { xs: 2, md: 0 },
                      px: 2.5,
                      py: 1.5,
                      borderRadius: "16px",
                      bgcolor: "primary.50",
                      border: "1px solid",
                      borderColor: "primary.100",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        animation: "pulse 2s infinite",
                      }}
                    >
                      {/* pulse animation remains */}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "primary.dark",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Sorted:
                    </Typography>
                    <Chip
                      icon={
                        filters.sortDirection === "ASC" ? (
                          <ArrowUpwardIcon fontSize="small" />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" />
                        )
                      }
                      label={filters.sortDirection}
                      size="small"
                      color="primary"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        height: 26,
                        borderRadius: "12px",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleSortChange("", "")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {/* Helper Text */}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "text.secondary",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  opacity: 0.8,
                }}
              >
                {filters.sortBy === "lastDateToApply"
                  ? `Sorted by deadline ${
                      filters.sortDirection === "ASC"
                        ? "ASC (earliest first)"
                        : "DESC (latest first)"
                    }`
                  : "Click ASC or DESC to sort by application deadline"}
              </Typography>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Opportunities List */}
            <Box sx={{ mb: 3 }}>
              {opportunities.map((opportunity, index) => {
                return (
                  <Card
                    key={index}
                    sx={{
                      mb: 2,
                      position: "relative",
                      boxShadow:
                        opportunity.feature === "Yes"
                          ? "0 4px 16px rgba(30, 58, 95, 0.2)"
                          : "0 2px 8px rgba(0,0,0,0.1)",
                      borderRadius: 2,
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow:
                          opportunity.feature === "Yes"
                            ? "0 8px 24px rgba(30, 58, 95, 0.3)"
                            : "0 4px 12px rgba(0,0,0,0.15)",
                      },
                      cursor: "pointer",
                      borderLeft:
                        opportunity.feature === "Yes"
                          ? "4px solid #ff8c00"
                          : "none",
                    }}
                    onClick={() => {
                      setOpportunities((prev) =>
                        prev.map((opp) =>
                          opp.id === opportunity.id
                            ? { ...opp, expanded: !opp.expanded }
                            : opp
                        )
                      );
                    }}
                  >
                    {!opportunity.status && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(255,255,255,0.6)",
                          backdropFilter: "grayscale(0.8)",
                          zIndex: 2,
                          pointerEvents: "none",
                        }}
                      ></Box>
                    )}
                    {isAdmin && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          display: "flex",
                          gap: 0.5,
                          zIndex: 1,
                          alignItems: "center",
                        }}
                      >
                        <Tooltip
                          title={opportunity.status ? "Deactivate" : "Activate"}
                        >
                          <Switch
                            size="small"
                            checked={opportunity.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(opportunity);
                            }}
                            sx={{
                              "& .MuiSwitch-switchBase": {
                                color: "#9e9e9e",
                                "&.Mui-checked": {
                                  color: "#4caf50",
                                },
                                "&.Mui-checked + .MuiSwitch-track": {
                                  backgroundColor: "#4caf50",
                                },
                              },
                              "& .MuiSwitch-track": {
                                backgroundColor: "#bdbdbd",
                                opacity: 0.8,
                              },
                              "&:hover .MuiSwitch-switchBase:not(.Mui-checked)":
                                {
                                  color: "#757575",
                                },
                              "&:hover .MuiSwitch-switchBase.Mui-checked": {
                                color: "#388e3c",
                              },
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(opportunity);
                            }}
                            sx={{
                              bgcolor: "rgba(255, 255, 255, 0.9)",
                              backdropFilter: "blur(4px)",
                              border: "1px solid rgba(255, 255, 255, 0.2)",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 1)",
                                transform: "scale(1.05)",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              },
                              transition: "all 0.2s ease-in-out",
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <CardContent sx={{ pt: 3, pb: 3, position: "relative" }}>
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 16,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {opportunity.lastDateToApply && (
                          <Chip
                            label={`Last Date to Apply: ${formatDisplayDate(
                              opportunity.lastDateToApply
                            )}`}
                            size="small"
                            sx={{
                              bgcolor: "#e3f2fd",
                              color: "#0d47a1",
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              borderRadius: "8px",
                              px: 1.5,
                              py: 0.5,
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.75rem",
                          }}
                        >
                          {timeAgo(opportunity.created_at)}
                        </Typography>
                      </Box>

                      <Grid container spacing={0} alignItems="center">
                        <Grid item xs={12} md={1}>
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor:
                                opportunity.feature === "Yes"
                                  ? "#ff8c00"
                                  : "primary.main",
                              color: "#fff",
                              fontSize: "1.3rem",
                              fontWeight: 600,
                            }}
                          >
                            {(() => {
                              const name = (opportunity.name ?? "")
                                .normalize("NFKD")
                                .replace(/[^\p{L}\p{N} ]/gu, "")
                                .trim();
                              return name ? name.charAt(0).toUpperCase() : "O";
                            })()}
                          </Avatar>
                        </Grid>

                        <Grid item xs={12} md={11}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: "primary.main",
                              }}
                            >
                              {opportunity.name}
                            </Typography>
                            {opportunity.feature === "Yes" && (
                              <Chip
                                label="Featured"
                                size="small"
                                color="warning"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {opportunity.source}
                          </Typography>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Chip
                              label={opportunity.category}
                              size="small"
                              sx={{
                                bgcolor: "#EEF2FF",
                                fontWeight: 500,
                              }}
                            />
                            <Chip
                              label={opportunity.subCategory}
                              size="small"
                              sx={{
                                bgcolor: "#EEF2FF",
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Expanded Content */}
                      <Collapse in={opportunity.expanded}>
                        <Box
                          sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}
                        >
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2, lineHeight: 1.6 }}
                          >
                            {opportunity.shortDescription ||
                              "No description available."}
                          </Typography>
                          <Button
                            variant="contained"
                            href={opportunity.hyperlink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              bgcolor: "primary.main",
                              "&:hover": {
                                bgcolor: "primary.main",
                              },
                            }}
                          >
                            Learn More
                          </Button>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {/* Rows per Page Selector */}
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2, mb: 3 }}
            >
              <Grid item>
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <InputLabel>Rows per page</InputLabel>
                  <Select
                    value={rowsPerPage}
                    label="Rows per page"
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {[5, 10, 25, 50].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(currentPage * rowsPerPage, pagination.total)} of{" "}
                  {pagination.total} entries
                </Typography>
              </Grid>
            </Grid>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 2,
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  sx={{ minWidth: "80px" }}
                >
                  Previous
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {getPageNumbers().map((pageNum, index) => (
                    <React.Fragment key={index}>
                      {pageNum === "..." ? (
                        <Typography
                          variant="body2"
                          sx={{ px: 1, color: "text.secondary" }}
                        >
                          ...
                        </Typography>
                      ) : (
                        <Button
                          variant={
                            currentPage === pageNum ? "contained" : "outlined"
                          }
                          onClick={() => setCurrentPage(pageNum)}
                          sx={{
                            minWidth: "40px",
                            bgcolor:
                              currentPage === pageNum
                                ? "primary.main"
                                : "transparent",
                          }}
                        >
                          {pageNum}
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                </Box>

                <Button
                  variant="outlined"
                  disabled={currentPage === pagination.totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  sx={{ minWidth: "80px" }}
                >
                  Next
                </Button>
              </Box>
            )}

            {/* Status Change Confirmation Dialog */}
            <Dialog
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {selectedOpp?.status
                  ? "Deactivate Opportunity"
                  : "Activate Opportunity"}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Are you sure you want to{" "}
                  {selectedOpp?.status ? "deactivate" : "activate"} this
                  opportunity?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleConfirmToggle}
                  autoFocus
                  variant="contained"
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{
                  width: "100%",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  borderRadius: 2,
                }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Container>
        </>
      )}
      <Footer />
    </>
  );
};

export default OpportunityList;
